import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { listTags, createTag } from "api/endpoints/tags";

/**
 * Batch 2.3 — <TagPicker>. Free-solo multi-select over the website's content
 * tags → `tag_ids`. Typing a new tag and pressing Enter creates it on the fly
 * via createTag, then selects the returned id.
 */
function TagPicker({ value, onChange, websiteId }) {
  const [tags, setTags] = useState([]); // [{ content_tag_id, name }]

  useEffect(() => {
    if (!websiteId) return;
    listTags(websiteId)
      .then(setTags)
      .catch(() => setTags([]));
  }, [websiteId]);

  // Map the controlled id list back to tag objects for the Autocomplete value.
  const selected = value.map(
    (id) => tags.find((t) => t.content_tag_id === id) || { content_tag_id: id, name: id }
  );

  const handleChange = async (event, nextValue) => {
    const ids = [];
    for (let i = 0; i < nextValue.length; i += 1) {
      const item = nextValue[i];
      if (typeof item !== "string") {
        ids.push(item.content_tag_id);
      } else {
        const name = item.trim();
        const existing = name && tags.find((t) => t.name.toLowerCase() === name.toLowerCase());
        if (existing) {
          ids.push(existing.content_tag_id);
        } else if (name) {
          try {
            const created = await createTag(websiteId, { name });
            setTags((prev) => [...prev, created]);
            ids.push(created.content_tag_id);
          } catch {
            // ignore a failed create — the tag simply is not added
          }
        }
      }
    }
    onChange(ids);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={tags}
      value={selected}
      onChange={handleChange}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
      isOptionEqualToValue={(option, val) => option.content_tag_id === val.content_tag_id}
      renderInput={(params) => (
        <TextField {...params} label="Tags" placeholder="Add a tag and press Enter" />
      )}
    />
  );
}

TagPicker.defaultProps = {
  value: [],
  websiteId: undefined,
};

TagPicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  websiteId: PropTypes.string,
};

export default TagPicker;
